import { z } from 'zod'

// Зона обязана резолвиться: молчаливый откат на UTC сдвинул бы границы суток на
// часы, поэтому кривое имя валит парс конфига — как на бэкенде оно валит старт.
const resolvableZone = (tz: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/

const RuntimeConfigSchema = z.object({
  API_BASE_URL: z.string().default('/api'),
  // Единый SSO хаба (00-architecture § 3): origin employee-backend (id.stit.tech),
  // куда уходит неаутентифицированный (/auth/login|logout). Дефолт '' (same-origin,
  // MSW/dev). В проде — https://id.stit.tech (compose → window.__APP_CONFIG__).
  ID_BASE_URL: z.string().default(''),
  // Контур заказчика (00-architecture § 3): origin company-backend — туда уходит
  // продление (/auth/customer/refresh) и выход (/auth/customer/logout) заказчика,
  // мимо api-клиента продукта. Дефолт '' (same-origin, MSW/dev). В проде —
  // https://api.hub.stit.tech; апекс продукта обязан быть в COMPANY_CORS_ORIGINS,
  // иначе preflight режется и продление молча вырождается в разлогин.
  HUB_API_BASE_URL: z.string().default(''),
  // Единая точка входа экосистемы (22-unified-login): сотрудник уходит оттуда в
  // Authentik, заказчик вводит логин и пароль. По 401 продукт не знает, кто перед
  // ним, поэтому ведёт сюда, а не на id.stit.tech. Своей формы входа у task нет —
  // дефолт указывает на прод-страницу, падать локально некуда.
  HUB_LOGIN_URL: z.string().default('https://hub.stit.tech/login'),
  // Зона организации (`APP_TZ` бэкенда, IANA-имя) — зона календарных суток и
  // срока: «сегодня», «просрочено», «за период» обязаны значить одно и то же для
  // всех, иначе экран и отчёт разъезжаются с сервером. Дефолт совпадает с
  // дефолтом бэка — расхождение сдвинуло бы границы суток. Момент («когда
  // началось», «когда пришло») в этой зоне не показывается: его зона — зона
  // устройства смотрящего, и конфига она не требует (`momentZone`).
  APP_TZ: z
    .string()
    .min(1)
    .refine(resolvableZone, { message: 'APP_TZ: неизвестная IANA-зона' })
    .default('Europe/Moscow'),
  // Час, которым срок «на весь день» превращается в момент: то же значение
  // `.env`, что бэкенд читает как `NOTIFIER_DEADLINE_HOUR`, — им воркер шлёт
  // «дедлайн наступил», и UI обязан красить и сортировать по нему же. Час
  // настраиваемый, поэтому приезжает рантаймом, а не живёт литералом: смена в
  // `.env` иначе развела бы алерт и экран.
  ALL_DAY_DUE_TIME: z
    .string()
    .refine((v) => HH_MM.test(v), { message: 'ALL_DAY_DUE_TIME: ожидается HH:MM' })
    .default('19:00'),
  API_MODE: z.enum(['mock', 'real']).default('mock'),
  MOCK_SCENARIO: z
    .enum([
      'empty',
      'populated',
      'authenticated',
      'customer',
      'error',
      'running-timer',
      'idle-pending',
      'mentions',
    ])
    .default('populated'),
  MOCK_DEVTOOLS: z.boolean().default(false),
  SENTRY_DSN: z.string().default(''),
  SENTRY_ENVIRONMENT: z.string().default('development'),
  RELEASE: z.string().default('dev'),
})

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>

declare global {
  interface Window {
    __APP_CONFIG__?: unknown
  }
}

function readConfig(): RuntimeConfig {
  const raw: unknown = typeof window === 'undefined' ? undefined : window.__APP_CONFIG__
  const fromWindow = raw !== null && typeof raw === 'object' ? raw : {}
  const fromEnv = {
    APP_TZ: import.meta.env.VITE_APP_TZ,
    ALL_DAY_DUE_TIME: import.meta.env.VITE_ALL_DAY_DUE_TIME,
    ID_BASE_URL: import.meta.env.VITE_ID_BASE_URL,
    HUB_API_BASE_URL: import.meta.env.VITE_HUB_API_BASE_URL,
    HUB_LOGIN_URL: import.meta.env.VITE_HUB_LOGIN_URL,
    API_MODE: import.meta.env.VITE_API_MODE,
    MOCK_SCENARIO: import.meta.env.VITE_MOCK_SCENARIO,
    MOCK_DEVTOOLS: import.meta.env.VITE_MOCK_DEVTOOLS === '1',
  }
  return RuntimeConfigSchema.parse({ ...fromEnv, ...fromWindow })
}

export const runtimeConfig = readConfig()
